/*
 * <dlux-points-redeem>: one-tap DLUX Rewards (Smile.io) redemption in the cart drawer.
 * Markup and the data it reads: snippets/dlux-points-redeem.liquid.
 *
 * The drawer re-renders .drawer__inner on every cart change, which replaces this
 * element. Everything that must outlive a re-render (Smile data, an in-flight
 * purchase, the last message) lives in module state below; each new element
 * re-hydrates from it in connectedCallback without calling Smile again.
 *
 * Smile.js on this store loads lazily. Smile's "lite" launcher
 * (js.smile.io/v1/smile-lite-*.js, read 1 Nov 2026) defines window.loadSmileSdk():
 * no arguments, no return value. It sets window.__smile_include_sdk__ and loads
 * smile-shopify-*.js + vendor-core-*.js once; a second call is a no-op, and it never
 * opens the panel. Once the full bundle is up it redefines loadSmileSdk as an async
 * function that loads the SDK on demand, so a shopper who opened the Smile panel
 * before the drawer still gets window.Smile from our call. We call it only for a
 * signed-in customer and only when the drawer opens with items in it (D5), then wait
 * up to 5 s for `smile-js-initialized`. While Smile answers the element holds one
 * row of height so the Checkout button does not jump. No SDK, an error or a slow
 * answer: the element hides and the drawer looks exactly as it did before.
 *
 * D4: with a line-level discount on a paid line (Set saving, Buy more save more:
 * product discounts that refuse order discounts), Liquid renders the element with
 * data-blocked and a plain line. The script then loads nothing and shows no button.
 *
 * Smile.js fields used. The points product was read on the live store on 29 Oct 2026;
 * the rest come from Smile's JS SDK schema and are re-checked signed in (see
 * docs/points-redeem/test-report.md):
 *   Smile.customerPointsWallet.preloaded() / .get()   -> { balance }
 *   Smile.pointsProducts.get()                        -> [{ id, exchangeType: 'variable',
 *       variablePointsStep, variablePointsStepRewardValue, variablePointsMin, variablePointsMax }]
 *   Smile.pointsProducts.purchase(id, { pointsToSpend }) -> { pointsSpent, rewardFulfillment: { id, code, name } }
 *   Smile.rewardFulfillments.get()                    -> [{ id, code, name, fulfillmentStatus, usedAt, expiresAt }]
 *
 * Applying a code: POST /cart/update.js { discount } REPLACES the cart's whole code
 * list, so the current codes are read from /cart.js first and sent back with the new
 * one. The write goes through window.fetch, so assets/dlux-cart-gift.js sees it and
 * adds or removes the gift in the same pass, then the drawer re-renders through the
 * same path the gift module uses (cartUpdate publish + CartDrawer.renderContents).
 */
(function () {
  'use strict';

  if (customElements.get('dlux-points-redeem')) return;

  var SMILE_TIMEOUT_MS = 5000; // waiting for smile-js-initialized
  var NO_LOADER_MAX = 2; // drawer opens with no loadSmileSdk on the page before giving up
  var CALL_TIMEOUT_MS = 8000; // the three Smile API calls together
  var PURCHASE_TIMEOUT_MS = 15000;
  // After a purchase Smile rejected outright: two unchanged balance reads 2 s apart
  // mean nothing was spent. After a timeout or a network drop the purchase may still
  // be landing, so the unchanged reads must span longer than the purchase timeout.
  var VERIFY_TRIES = 4;
  var VERIFY_GAP_MS = 2000;
  var VERIFY_NEED = 2;
  var VERIFY_TRIES_SLOW = 5;
  var VERIFY_GAP_SLOW_MS = 4000;
  var VERIFY_NEED_SLOW = 4;
  var DATA_TTL_MS = 30000;
  var FLASH_MS = 20000;
  // A block that appears while the drawer is already open ignores taps this long,
  // so a tap aimed at what it pushed down (Checkout) cannot land on "Use $X".
  var ARM_MS = 700;
  var REWARD_PREFIX = 'REWARDS-';

  var COPY = {
    // Dollars first (Dominic, 1 Nov 2026). 100 points = $1, so a points balance is also
    // a cents figure for money().
    points: function (balance) { return 'You have ' + money(balance) + ' in points.'; },
    use: function (dollars) { return 'Use $' + dollars; },
    ready: function (dollars) {
      return dollars ? '$' + dollars + ' reward ready.' : 'You have a reward ready.';
    },
    apply: function (dollars) { return dollars ? 'Apply $' + dollars : 'Apply reward'; },
    pointsLeft: function (balance) { return money(balance) + ' left in points.'; },
    fewer: 'Use less',
    removed: 'Your code stays saved for next time.',
    changed: 'Your cart changed, so the amount did too.',
    error: 'Something went wrong. Your points are safe. Try again.',
    saved: function (dollars) {
      return (dollars ? 'We saved your $' + dollars + ' code.' : 'We saved your code.') + ' You can use it at checkout.';
    },
    conflict: function (dollars) {
      return 'Your points code does not work with your other code. We kept the other one. ' +
        (dollars ? 'Your $' + dollars + ' code is saved for next time.' : 'Your points code is saved for next time.');
    },
    tooBig: function (dollars) {
      return '$' + dollars + ' reward ready. Use it on an order over $' + dollars + '.';
    },
    pending: 'Your reward is on its way. Refresh the page in a minute to use it.',
    unknown: 'Something went wrong. Check your points in DLUX Rewards before you try again.',
    copy: 'Copy',
    copied: 'Copied',
    holdToCopy: 'Tap and hold to copy.',
    working: 'Working',
    underShip: function (threshold, fee) {
      return 'Using points takes you under ' + threshold + ', so shipping is ' + fee + '.';
    },
    underGift: function (threshold) {
      return 'Using points takes you under ' + threshold + ', so the free gift comes off.';
    },
    underBoth: function (shipThreshold, fee, giftThreshold) {
      return 'Using points takes you under ' + giftThreshold + ', so the free gift comes off. Under ' +
        shipThreshold + ', shipping is ' + fee + '.';
    },
  };

  // ── Module state (survives drawer re-renders) ─────────────────────────────

  var state = {
    data: null, // { balance, product, fulfillments, at }
    loading: null, // Promise while Smile data is being fetched
    failed: false, // Smile missing or erroring: stay hidden for this page
    loadingGen: 0, // state.gen when `loading` started
    gen: 0, // bumped by every purchase; older loads may not overwrite `data`
    busy: false, // a purchase or cart write is in flight
    lockBuy: false, // a purchase could not be verified: no buy button for this page
    payable: 0, // payable cents read from /cart.js at the last tap
    payableAt: 0,
    // { kind: 'removed' | 'changed' | 'error' | 'saved' | 'conflict' | 'pending' | 'unknown', at, code, dollars }
    flash: null,
    known: {}, // code -> dollars, for codes bought on this page
  };

  var smileReady = null;
  var smileInitialized = false;
  document.addEventListener('smile-js-initialized', function () {
    smileInitialized = true;
  });

  // ── Helpers ───────────────────────────────────────────────────────────────

  function money(cents) {
    var dollars = cents / 100;
    return '$' + (dollars % 1 === 0 ? String(dollars) : dollars.toFixed(2));
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('timeout')); }, ms);
      }),
    ]);
  }

  function sameCode(a, b) {
    return String(a || '').toUpperCase() === String(b || '').toUpperCase();
  }

  function isRewardCode(code) {
    return String(code || '').toUpperCase().indexOf(REWARD_PREFIX) === 0;
  }

  function track(event, data) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var payload = { event: event, promo_location: 'cart_drawer' };
        for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
        window.dataLayer.push(payload);
      }
    } catch (_) {}
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function drawerOpen() {
    var d = drawer();
    return !!(d && d.classList.contains('active'));
  }

  function renderAll() {
    document.querySelectorAll('dlux-points-redeem').forEach(function (el) {
      if (typeof el.render === 'function') el.render();
    });
  }

  // ── Smile ─────────────────────────────────────────────────────────────────

  function smileUsable() {
    var S = window.Smile;
    return !!(S && S.customerPointsWallet && S.pointsProducts && S.rewardFulfillments);
  }

  // Resolves once Smile.js is initialised, rejects after SMILE_TIMEOUT_MS. A rejection
  // is kept for the page, so later re-renders do not wait another 5 s each. One
  // exception: Smile's lite launcher (which defines loadSmileSdk) only arrives after
  // window load, so a drawer opened early may find no loader at all. Then the wait
  // listens for `smile-lite-loaded` and polls for the function inside the same budget,
  // and a miss lets the next drawer open try again (NO_LOADER_MAX times).
  var noLoaderMisses = 0;
  function ensureSmile() {
    if (smileReady) return smileReady;
    smileReady = new Promise(function (resolve, reject) {
      if (smileUsable() && smileInitialized) return resolve(window.Smile);
      var called = false;
      var done = false;
      var poll = null;
      var timer = null;
      function finish(ok, err) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (poll) clearInterval(poll);
        document.removeEventListener('smile-lite-loaded', callLoader);
        if (ok) resolve(window.Smile);
        else reject(err);
      }
      function callLoader() {
        if (called || typeof window.loadSmileSdk !== 'function') return;
        called = true;
        if (poll) clearInterval(poll);
        try {
          window.loadSmileSdk();
        } catch (_) {}
      }
      timer = setTimeout(function () {
        // Smile already there but its event fired before this script listened:
        // try it as is. Its calls reject if it is not ready, which hides the block.
        if (smileUsable()) return finish(true);
        if (!called) {
          noLoaderMisses += 1;
          if (noLoaderMisses < NO_LOADER_MAX) smileReady = null;
          return finish(false, new Error(noLoaderMisses < NO_LOADER_MAX ? 'no loader' : 'smile timeout'));
        }
        finish(false, new Error('smile timeout'));
      }, SMILE_TIMEOUT_MS);
      document.addEventListener(
        'smile-js-initialized',
        function () {
          if (smileUsable()) finish(true);
          else finish(false, new Error('smile unusable'));
        },
        { once: true }
      );
      callLoader();
      if (!called) {
        document.addEventListener('smile-lite-loaded', callLoader);
        poll = setInterval(callLoader, 250);
      }
      // Already loaded and initialised before we listened.
      if (smileUsable() && smileInitialized) finish(true);
    });
    return smileReady;
  }

  function pickProduct(products) {
    if (!Array.isArray(products)) return null;
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (p && p.exchangeType === 'variable' && p.variablePointsStep > 0 && p.variablePointsStepRewardValue > 0) {
        return p;
      }
    }
    return null;
  }

  // Each purchase bumps state.gen. A load that started before the bump cannot write
  // its (older) snapshot over what the purchase changed, and is not reused.
  function loadData(force) {
    if (state.failed) return Promise.reject(new Error('smile failed'));
    var fresh = state.data && Date.now() - state.data.at < DATA_TTL_MS;
    if (fresh && !force) return Promise.resolve(state.data);
    if (state.loading && state.loadingGen === state.gen) return state.loading;
    var gen = state.gen;
    var first = !state.data;
    var load = ensureSmile().then(function (S) {
      var preloaded = null;
      try {
        preloaded = S.customerPointsWallet.preloaded();
      } catch (_) {}
      var wallet = preloaded && first && !force ? Promise.resolve(preloaded) : S.customerPointsWallet.get();
      // The API calls get their own budget, after Smile is ready.
      return withTimeout(Promise.all([wallet, S.pointsProducts.get(), S.rewardFulfillments.get()]), CALL_TIMEOUT_MS);
    });
    var p = load
      .then(function (res) {
        var balance = res[0] && typeof res[0].balance === 'number' ? res[0].balance : null;
        if (balance === null) throw new Error('no balance');
        var data = {
          balance: balance,
          product: pickProduct(res[1]),
          fulfillments: Array.isArray(res[2]) ? res[2] : [],
          at: Date.now(),
        };
        if (gen === state.gen) state.data = data;
        return data;
      })
      .catch(function (err) {
        // Keep showing data we already have; with none, hide for the page. "no
        // loader" (Smile's launcher not on the page yet) hides for now and lets the
        // next drawer open try again.
        if (!state.data && !(err && err.message === 'no loader')) state.failed = true;
        throw err;
      });
    state.loading = p;
    state.loadingGen = gen;
    var clear = function () {
      if (state.loading === p) state.loading = null;
    };
    p.then(clear, clear);
    return p;
  }

  // Dollar value of a reward code. Smile's fulfillment object has no value field, so
  // use what we bought on this page, else the "$N off" in its name.
  function codeDollars(code, name) {
    if (state.known[code]) return state.known[code];
    var m = /\$\s?(\d+(?:\.\d{1,2})?)/.exec(name || '');
    if (!m) return 0;
    var n = Number(m[1]);
    return n % 1 === 0 ? n : Number(n.toFixed(2));
  }

  // Unused Smile reward codes, newest first. usedAt stays null while a code is
  // unused. Expired codes and the statuses Smile documents as not usable are skipped;
  // any other status is kept, so an unexpected value can never hide a held code and
  // lead to a second purchase.
  var DEAD_STATUSES = { pending: 1, cancelled: 1, failed: 1 };
  function unusedRewards(data, exceptCode) {
    var now = Date.now();
    return data.fulfillments
      .filter(function (f) {
        if (!f || !f.code || !isRewardCode(f.code)) return false;
        if (f.usedAt) return false;
        if (f.fulfillmentStatus && DEAD_STATUSES[f.fulfillmentStatus]) return false;
        if (f.expiresAt && Date.parse(f.expiresAt) <= now) return false;
        if (exceptCode && sameCode(f.code, exceptCode)) return false;
        return true;
      })
      .sort(function (a, b) {
        return Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0);
      })
      .map(function (f) {
        return { code: f.code, dollars: codeDollars(f.code, f.name) };
      });
  }

  // The most the shopper can use: whole steps that fit the balance, the product's
  // max and the order total. Never more than the order is worth.
  function bestOffer(data, payableCents) {
    var p = data.product;
    if (!p) return null;
    var step = p.variablePointsStep;
    var value = p.variablePointsStepRewardValue; // dollars per step
    var steps = Math.floor(data.balance / step);
    if (p.variablePointsMax) steps = Math.min(steps, Math.floor(p.variablePointsMax / step));
    steps = Math.min(steps, Math.floor(payableCents / 100 / value));
    if (steps < 1) return null;
    var points = steps * step;
    if (p.variablePointsMin && points < p.variablePointsMin) return null;
    var dollars = steps * value;
    return { points: points, dollars: Number(dollars.toFixed(2)), productId: p.id };
  }

  // ── Cart ──────────────────────────────────────────────────────────────────

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCart() {
    return fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  // Same figure as snippets/dlux-points-redeem.liquid: total after discounts, minus
  // the gift line (zeroed at checkout).
  function payableOf(cart) {
    var cents = cart.total_price || 0;
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._gift === 'true') cents -= item.final_line_price || 0;
    });
    return Math.max(0, cents);
  }

  // Replaces the cart's codes with `codes` and re-renders the drawer the way the
  // rest of the theme does after a cart write.
  function writeCodes(codes) {
    var d = drawer();
    var sections =
      d && typeof d.getSectionsToRender === 'function'
        ? d.getSectionsToRender().map(function (s) { return s.id; })
        : [];
    var url = (window.routes && window.routes.cart_update_url) || cartRoot() + 'cart/update';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/update ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (!cart || cart.status || cart.errors) throw new Error('cart/update rejected');
        var published =
          typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dlux-points-redeem', cartData: cart })
            : Promise.resolve();
        return Promise.resolve(published).then(function () {
          if (d && typeof d.renderContents === 'function' && cart.sections) {
            d.renderContents(cart, { open: d.classList.contains('active') });
          }
          return cart;
        });
      });
  }

  function cartCodes(cart) {
    return (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []).map(function (c) {
      return c.code;
    });
  }

  function applicableCodes(cart) {
    var map = {};
    (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []).forEach(function (c) {
      if (c.applicable !== false) map[String(c.code).toUpperCase()] = true;
    });
    return map;
  }

  // Adds `code` to whatever codes the cart already has. Resolves to
  //   'applied'   Shopify lists it as applicable;
  //   'rejected'  Shopify will not apply it (it stays on the cart for checkout);
  //   'conflict'  it knocked off another code that was applying, so the old list
  //               was put back and the reward code stays saved for later.
  function applyCode(code) {
    var before;
    return getCart()
      .then(function (cart) {
        before = cart;
        var others = cartCodes(cart).filter(function (c) { return !sameCode(c, code); });
        return writeCodes(others.concat([code]));
      })
      .then(function (cart) {
        // Read the codes back from /cart.js if this response does not carry them.
        return Array.isArray(cart.discount_codes) ? cart : getCart();
      })
      .then(function (cart) {
        var now = applicableCodes(cart);
        if (!now[String(code).toUpperCase()]) return 'rejected';
        var was = applicableCodes(before);
        var lost = Object.keys(was).filter(function (c) { return !now[c] && !sameCode(c, code); });
        if (!lost.length) return 'applied';
        return writeCodes(cartCodes(before).filter(function (c) { return !sameCode(c, code); })).then(function () {
          return 'conflict';
        });
      });
  }

  function removeCode(code) {
    return getCart().then(function (cart) {
      return writeCodes(cartCodes(cart).filter(function (c) { return !sameCode(c, code); }));
    });
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  function finishApply(code, dollars) {
    return applyCode(code)
      .then(function (result) {
        state.flash =
          result === 'applied'
            ? null
            : { kind: result === 'conflict' ? 'conflict' : 'saved', at: Date.now(), code: code, dollars: dollars };
        track('points_redeem_' + result, { reward_value: dollars });
      })
      .catch(function () {
        state.flash = { kind: 'saved', at: Date.now(), code: code, dollars: dollars };
        track('points_redeem_apply_error', { reward_value: dollars });
      });
  }

  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // After a purchase that failed, timed out or came back without a code, find out
  // from Smile what really happened before the shopper can tap again:
  //   a new unused code appeared   -> apply it (points were spent, the code is used);
  //   balance unchanged, twice     -> "Your points are safe. Try again.";
  //   balance dropped, no code yet -> "on its way", no buy button for this page;
  //   Smile will not answer        -> no buy button for this page.
  function verifyPurchase(offer, before, spent, slow) {
    var unchanged = 0;
    var attempt = 0;
    var gap = slow ? VERIFY_GAP_SLOW_MS : VERIFY_GAP_MS;
    var tries = slow ? VERIFY_TRIES_SLOW : VERIFY_TRIES;
    var need = slow ? VERIFY_NEED_SLOW : VERIFY_NEED;
    function giveUp() {
      state.lockBuy = true;
      state.flash = { kind: spent ? 'pending' : 'unknown', at: Date.now() };
      track('points_redeem_unverified', { spent: !!spent });
    }
    function next() {
      attempt += 1;
      return loadData(true).then(
        function (data) {
          var fresh = unusedRewards(data).filter(function (r) { return !before.codes[r.code.toUpperCase()]; })[0];
          if (fresh) {
            if (!state.known[fresh.code]) state.known[fresh.code] = fresh.dollars || offer.dollars;
            return finishApply(fresh.code, state.known[fresh.code]);
          }
          if (data.balance < before.balance) spent = true;
          if (!spent) unchanged += 1;
          if (unchanged >= need) {
            state.flash = { kind: 'error', at: Date.now() };
            return;
          }
          if (attempt >= tries) return giveUp();
          return sleep(gap).then(next);
        },
        function () {
          if (attempt >= tries) return giveUp();
          return sleep(gap).then(next);
        }
      );
    }
    return next();
  }

  // One tap = one purchase. state.busy is module-wide, so a double tap, or a tap on
  // a freshly re-rendered button, can never start a second purchase. Before buying,
  // re-read Smile and the cart: a code bought elsewhere (the Smile panel, another
  // tab) is offered instead, and a cart that changed since the last render gets a
  // new amount instead of a purchase.
  function redeem(tapped) {
    if (state.busy || state.lockBuy || !tapped) return;
    state.busy = true;
    state.flash = null;
    renderAll();
    track('points_redeem_click', { reward_value: tapped.dollars, points: tapped.points });
    Promise.all([loadData(true), getCart()])
      .then(
        function (res) {
          var data = res[0];
          var cart = res[1];
          state.payable = payableOf(cart);
          state.payableAt = Date.now();
          if (unusedRewards(data).length) return; // the re-render offers the held code
          var offer = bestOffer(data, state.payable);
          if (!offer || offer.dollars !== tapped.dollars || offer.points !== tapped.points) {
            // Nothing bought: the re-render shows the new amount, with a line saying why.
            state.flash = { kind: 'changed', at: Date.now() };
            return;
          }
          var before = { balance: data.balance, codes: {} };
          data.fulfillments.forEach(function (f) {
            if (f && f.code) before.codes[String(f.code).toUpperCase()] = true;
          });
          return buy(offer, before);
        },
        function () {
          // Nothing was bought: the check before buying failed.
          state.flash = { kind: 'error', at: Date.now() };
        }
      )
      .catch(function () {
        state.flash = { kind: 'error', at: Date.now() };
      })
      .then(function () {
        state.busy = false;
        renderAll();
      });
  }

  function buy(offer, before) {
    state.gen += 1;
    var purchase;
    try {
      purchase = window.Smile.pointsProducts.purchase(offer.productId, { pointsToSpend: offer.points });
    } catch (e) {
      purchase = Promise.reject(e);
    }
    // A purchase that never settles is treated like a failure: verifyPurchase finds
    // the code if it did go through. A late answer is ignored; nothing re-buys.
    return withTimeout(Promise.resolve(purchase), PURCHASE_TIMEOUT_MS).then(
      function (result) {
        var rf = result && result.rewardFulfillment;
        var code = rf && rf.code;
        if (state.data) {
          state.data.balance = Math.max(0, state.data.balance - ((result && result.pointsSpent) || offer.points));
        }
        if (!code) return verifyPurchase(offer, before, true);
        state.known[code] = offer.dollars;
        if (state.data) state.data.fulfillments.push(rf);
        return finishApply(code, offer.dollars);
      },
      function (err) {
        track('points_redeem_error', { error: (err && err.message) || 'unknown' });
        // An outright Smile rejection carries a status/type; a timeout or a dropped
        // connection does not, and the purchase may still land, so verify slowly.
        var explicit = !!(err && typeof err === 'object' && err.message !== 'timeout' && (err.status || err.code || err.type));
        return verifyPurchase(offer, before, false, !explicit);
      }
    );
  }

  // Applies a code the shopper already holds. Spends no points. Checked against the
  // live cart first, so a code worth more than the order is never applied in one tap.
  function applyExisting(reward) {
    if (state.busy || !reward) return;
    state.busy = true;
    state.flash = null;
    renderAll();
    track('points_reward_apply_click', { reward_value: reward.dollars });
    getCart()
      .then(function (cart) {
        state.payable = payableOf(cart);
        state.payableAt = Date.now();
        if (reward.dollars && reward.dollars * 100 > state.payable) return;
        return finishApply(reward.code, reward.dollars);
      })
      .catch(function () {
        state.flash = { kind: 'error', at: Date.now() };
      })
      .then(function () {
        state.busy = false;
        renderAll();
      });
  }

  function remove(code) {
    if (state.busy || !code) return;
    state.busy = true;
    renderAll();
    removeCode(code)
      .then(
        function () {
          state.flash = { kind: 'removed', at: Date.now() };
          track('points_reward_remove', {});
        },
        function () {
          state.flash = { kind: 'error', at: Date.now() };
        }
      )
      .then(function () {
        state.busy = false;
        renderAll();
      });
  }

  function openSmilePanel(productId) {
    var d = drawer();
    if (d && typeof d.close === 'function') d.close();
    try {
      if (window.SmileUI && typeof window.SmileUI.openPanel === 'function') {
        window.SmileUI.openPanel({ deep_link: productId ? 'points_product:' + productId : 'points_products' });
        return;
      }
    } catch (_) {}
    window.location.hash = 'smile-points-products';
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error('execCommand returned false'));
      } catch (e) {
        reject(e);
      }
    });
  }

  // Asks Smile for the data (a no-op while fresh data is cached), paints the loading
  // placeholder straight away, then the result.
  function wake() {
    var p = loadData();
    renderAll();
    p.then(renderAll, renderAll);
  }

  // Wakes Smile only when the drawer opens with a signed-in element in it (D5). The
  // element exists only while the cart has items, and not at all for guests, so no
  // guest and no empty cart ever loads the SDK. Set up once per page. A reopen after
  // the data went stale refreshes it (e.g. points spent in the Smile panel meanwhile).
  var wakeArmed = false;
  function armWake() {
    if (wakeArmed) return;
    wakeArmed = true;
    var d = drawer();
    if (!d) return;
    new MutationObserver(function () {
      if (!d.classList.contains('active')) return;
      if (!document.querySelector('dlux-points-redeem:not([data-blocked])')) return;
      if (!state.data) {
        if (!state.failed) wake();
        return;
      }
      if (Date.now() - state.data.at >= DATA_TTL_MS) loadData(true).then(renderAll, function () {});
    }).observe(d, { attributes: true, attributeFilter: ['class'] });
  }

  // ── Element ───────────────────────────────────────────────────────────────

  class DluxPointsRedeem extends HTMLElement {
    connectedCallback() {
      this.connectedAt = Date.now();
      this.armedAt = 0;
      this.payable = Number(this.dataset.payableCents) || 0;
      this.blocked = !!this.dataset.blocked;
      this.appliedCode = this.dataset.appliedCode || '';
      this.appliedCents = Number(this.dataset.appliedCents) || 0;
      // Thresholds as the free-ship bar and the gift gate see them, from the same
      // drawer render. data-gwp-enabled is only "true" when the gift is in stock.
      var gwp = this.closest('.drawer__inner, #CartDrawer') || document;
      gwp = gwp.querySelector('[data-dlux-gwp]') || document.querySelector('[data-dlux-gwp]');
      this.fsCents = gwp ? Number(gwp.getAttribute('data-gwp-fs-cents')) || 0 : 0;
      this.giftCents =
        gwp && gwp.getAttribute('data-gwp-enabled') === 'true' ? Number(gwp.getAttribute('data-gwp-threshold-cents')) || 0 : 0;
      this.shipFee = this.dataset.shipFee || '';
      this.hidePromo = this.dataset.hidePromo === 'true';

      // The applied state is server rendered; only its Remove link needs wiring.
      var removeBtn = this.querySelector('[data-pr-remove]');
      if (removeBtn) {
        var code = this.appliedCode;
        removeBtn.addEventListener('click', function () { remove(code); });
      }

      if (this.blocked) {
        // D4: Liquid rendered the "can't be used with this saving" line. Nothing to
        // load, nothing to tap.
        this.hidden = false;
        return;
      }
      if (!state.failed && drawerOpen()) {
        wake();
      } else {
        this.render();
      }
      armWake();
    }

    // D1 for a code Liquid cannot see: a reward code that sits on the cart but is not
    // applying (saved or conflict state) would still be knocked off at checkout by the
    // SKIN5 code, so the copy-code nudge hides while that state shows.
    syncPromo() {
      if (!this.hidePromo) return;
      var scope = this.closest('.drawer__inner') || document;
      var promo = scope.querySelector('[data-dlux-cart-promo]');
      if (!promo) return;
      var f = state.flash;
      promo.hidden = !!(f && (f.kind === 'saved' || f.kind === 'conflict'));
    }

    // The cart as of the last tap if that is newer than this render (a quantity
    // change may still be on its way), else what Liquid rendered.
    payableNow() {
      return state.payableAt > this.connectedAt ? state.payable : this.payable;
    }

    render() {
      if (this.blocked) return;
      var wasHidden = this.hidden || this.getAttribute('data-pr-state') === 'loading';
      // Smile never answered: leave the server markup alone (applied state, if any).
      if (state.failed && !this.appliedCode) {
        this.removeAttribute('data-pr-state');
        // Remove works without Smile; still say the code is kept.
        var removed = state.flash && state.flash.kind === 'removed' && Date.now() - state.flash.at <= FLASH_MS;
        if (removed) {
          this.replaceChildren(this.para(COPY.removed, 'dlux-points-redeem__note', true));
          this.hidden = false;
        } else {
          this.hidden = true;
        }
        return;
      }
      this.syncPromo();
      var view = this.view();
      if (!view) {
        if (!this.appliedCode && !state.data && state.loading) {
          // Smile is being asked right now: hold one row of height so the Checkout
          // button does not jump when the text and button arrive.
          this.setAttribute('data-pr-state', 'loading');
          this.hidden = false;
          return;
        }
        this.removeAttribute('data-pr-state');
        this.hidden = !this.appliedCode;
        return;
      }
      this.removeAttribute('data-pr-state');
      this.replaceChildren.apply(this, view);
      this.hidden = view.length === 0;
      // Appeared late, under an open drawer: hold taps for a moment (see ARM_MS).
      if (wasHidden && !this.hidden && drawerOpen() && Date.now() - this.connectedAt > 150) {
        this.armedAt = Date.now() + ARM_MS;
      }
    }

    // Returns the nodes to show, or null to leave the element as it is.
    view() {
      var data = state.data;
      var flash = state.flash;
      // "Removed", "changed" and error lines are for the moment they happen, not every later render.
      if (flash && (flash.kind === 'removed' || flash.kind === 'changed' || flash.kind === 'error') && Date.now() - flash.at > FLASH_MS) {
        flash = state.flash = null;
      }

      // A reward is on the cart (server-rendered text and Remove link).
      if (this.appliedCode) {
        var text = this.querySelector('[data-pr-text]');
        if (text && data && !text.dataset.prLeft) {
          text.dataset.prLeft = '1';
          text.textContent = text.textContent.trim() + ' ' + COPY.pointsLeft(data.balance);
        }
        var btn = this.querySelector('[data-pr-remove]');
        if (btn) {
          btn.disabled = state.busy;
          btn.setAttribute('aria-busy', state.busy ? 'true' : 'false');
        }
        return null;
      }

      // A code we made but could not apply: hand it over to keep.
      if (flash && flash.kind === 'saved') return this.savedView(flash, COPY.saved(flash.dollars));
      if (flash && flash.kind === 'conflict') return this.savedView(flash, COPY.conflict(flash.dollars));

      var lockNote = flash && (flash.kind === 'pending' || flash.kind === 'unknown') ? COPY[flash.kind] : null;
      if (!data) return lockNote ? [this.para(lockNote, 'dlux-points-redeem__text', true)] : null;

      var payable = this.payableNow();
      var nodes = [];
      var reward = unusedRewards(data)[0];
      if (reward && reward.dollars && reward.dollars * 100 > payable) {
        // Single use: applying it here would throw the rest away.
        nodes.push(this.para(COPY.tooBig(reward.dollars), 'dlux-points-redeem__text'));
      } else if (reward) {
        nodes.push(this.para(COPY.ready(reward.dollars), 'dlux-points-redeem__text'));
        nodes.push(
          this.button(COPY.apply(reward.dollars), function () {
            applyExisting(reward);
          })
        );
        var note = this.thresholdNote(reward.dollars * 100);
        if (note) nodes.push(note);
      } else if (lockNote) {
        return [this.para(lockNote, 'dlux-points-redeem__text', true)];
      } else {
        var offer = bestOffer(data, payable);
        if (!offer) return flash && flash.kind === 'error' ? [this.para(COPY.error, 'dlux-points-redeem__note', true)] : [];
        nodes.push(this.para(COPY.points(data.balance), 'dlux-points-redeem__text'));
        nodes.push(
          this.button(COPY.use(offer.dollars), function () {
            redeem(offer);
          })
        );
        var fewer = document.createElement('button');
        fewer.type = 'button';
        fewer.className = 'dlux-points-redeem__link';
        fewer.textContent = COPY.fewer;
        fewer.disabled = state.busy;
        fewer.addEventListener('click', function () {
          openSmilePanel(offer.productId);
        });
        nodes.push(fewer);
        var ship = this.thresholdNote(offer.dollars * 100);
        if (ship) nodes.push(ship);
      }

      if (flash && flash.kind === 'removed') nodes.push(this.para(COPY.removed, 'dlux-points-redeem__note', true));
      if (flash && flash.kind === 'changed') nodes.push(this.para(COPY.changed, 'dlux-points-redeem__note', true));
      if (flash && flash.kind === 'error') nodes.push(this.para(COPY.error, 'dlux-points-redeem__note', true));
      if (lockNote) nodes.push(this.para(lockNote, 'dlux-points-redeem__note', true));
      return nodes;
    }

    savedView(flash, message) {
      var nodes = [this.para(message, 'dlux-points-redeem__text', true)];
      var wrap = document.createElement('div');
      wrap.className = 'dlux-points-redeem__saved';
      var input = document.createElement('input');
      input.type = 'text';
      input.readOnly = true;
      input.value = flash.code;
      input.className = 'dlux-points-redeem__code';
      input.setAttribute('aria-label', 'Your reward code');
      var hint = this.para(COPY.holdToCopy, 'dlux-points-redeem__note');
      hint.hidden = true;
      var copyBtn = this.button(COPY.copy, function () {
        copyText(flash.code).then(
          function () {
            copyBtn.textContent = COPY.copied;
            setTimeout(function () { copyBtn.textContent = COPY.copy; }, 2000);
          },
          function () {
            // iOS in-app browsers reject the clipboard write: select for tap and hold.
            hint.hidden = false;
            try {
              input.focus({ preventScroll: true });
              input.setSelectionRange(0, input.value.length);
              input.select();
            } catch (_) {}
          }
        );
      }, true);
      wrap.appendChild(input);
      wrap.appendChild(copyBtn);
      nodes.push(wrap, hint);
      return nodes;
    }

    // "Using points takes you under $50..." when the discount crosses a threshold
    // the shopper had reached. Both are judged on the post-discount, non-gift total.
    thresholdNote(discountCents) {
      var before = this.payableNow();
      var after = before - discountCents;
      var ship = this.fsCents > 0 && this.shipFee && before >= this.fsCents && after < this.fsCents;
      var gift = this.giftCents > 0 && before >= this.giftCents && after < this.giftCents;
      var text = null;
      if (ship && gift) text = COPY.underBoth(money(this.fsCents), this.shipFee, money(this.giftCents));
      else if (gift) text = COPY.underGift(money(this.giftCents));
      else if (ship) text = COPY.underShip(money(this.fsCents), this.shipFee);
      return text ? this.para(text, 'dlux-points-redeem__note') : null;
    }

    // `live` marks the lines that report an outcome (removed, error, saved) so a screen
    // reader hears them; the offer itself is not a live region, so a quantity change
    // does not re-announce it.
    para(text, className, live) {
      var p = document.createElement('p');
      p.className = className;
      p.textContent = text;
      if (live) p.setAttribute('role', 'status');
      return p;
    }

    button(label, onClick, ignoreBusy) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dlux-points-redeem__button';
      var busy = state.busy && !ignoreBusy;
      btn.disabled = busy;
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        var spin = document.createElement('span');
        spin.className = 'dlux-points-redeem__spinner';
        spin.setAttribute('aria-hidden', 'true');
        btn.appendChild(spin);
        var sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = COPY.working;
        btn.appendChild(sr);
      } else {
        btn.textContent = label;
      }
      var self = this;
      btn.addEventListener('click', function () {
        if (state.busy && !ignoreBusy) return;
        if (Date.now() < self.armedAt) return;
        onClick();
      });
      return btn;
    }
  }

  customElements.define('dlux-points-redeem', DluxPointsRedeem);
})();
